import React from 'react';
import NProgress from 'nprogress';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { useNotify } from '../../../hooks';
import { PromoCode } from '../../../models/index';
import { FormButtons } from '../../shared/Forms';
import { CREATE_PROMOCODE, UPDATE_PROMOCODE } from '../../../graphql/mutation';
import { validatePromoCodeForm } from '../../../lib/formValidations';
import SaleFormFields from './SaleFormFields';

interface SaleFormProps {
  sale?: PromoCode;
}

function SaleForm({ sale }: SaleFormProps) {
  const notify = useNotify();
  const router = useRouter();
  const [disabled, setDisabled] = React.useState(false);
  const [formState, setFormState] = React.useState<Partial<PromoCode>>(
    sale ?? {}
  );

  const [updatePromoCodeMutation] = useMutation<{
    updatePromoCode: { record: PromoCode };
  }>(UPDATE_PROMOCODE);

  const [createPromoCodeMutation] = useMutation<{
    createPromoCode: { record: PromoCode };
  }>(CREATE_PROMOCODE);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      NProgress.start();
      // disabled validation
      if (disabled) return;
      setDisabled(true);
      // validate form
      const error = validatePromoCodeForm(formState);
      if (error) {
        notify(error, 'error');
        return;
      }
      // build object
      const promoCodeData = {
        ...formState,
        value: Number(formState.value),
        active: true,
      };
      // create or update sale
      if (sale) {
        await updatePromoCode(promoCodeData);
      } else {
        await createPromoCode(promoCodeData);
      }
    } catch (error) {
      // duplicate code
      if (error.message.includes('duplicate key error')) {
        return notify('El código de promoción ya existe', 'error');
      }

      return notify(error.message, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  const createPromoCode = async (promoData: Partial<PromoCode>) => {
    const { data } = await createPromoCodeMutation({
      variables: {
        record: {
          ...promoData,
        },
      },
    });
    if (data?.createPromoCode?.record?._id) {
      notify('Promoción creada con éxito', 'success');
      router.push('/app/maintenance/sales');
    } else {
      notify('Ha ocurrido un error al crear la promoción', 'error');
    }
  };

  const updatePromoCode = async (promoData: Partial<PromoCode>) => {
    delete promoData._id;
    const { data } = await updatePromoCodeMutation({
      variables: {
        filter: {
          _id: sale._id,
        },
        record: {
          ...promoData,
          __typename: undefined,
        },
      },
    });
    if (data?.updatePromoCode?.record._id) {
      notify('Usuario actualizado con éxito', 'success');
      router.push('/app/maintenance/sales');
    } else {
      notify('Ha ocurrido un error al actualizar la promo ', 'error');
    }
  };

  const changePromoStatus = async () => {
    await updatePromoCode({
      active: !sale.active,
    });
  };

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      className="flex flex-col w-full space-y-10"
    >
      <SaleFormFields formState={formState} setFormState={setFormState} />
      <FormButtons
        type={sale ? 'update' : 'create'}
        toggleActive={changePromoStatus}
        active={sale?.active}
        disabled={disabled}
        onSubmit={onSubmit}
      />
    </form>
  );
}

export default SaleForm;
